import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import '../components/AdminLayout.css';

async function getFileUrl(note) {
  // Extract bucket + path from Supabase storage URL and create signed URL
  if (note.file_url && note.file_url.startsWith('http')) {
    const match = note.file_url.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/?]+)\/(.+?)(\?.*)?$/);
    if (match) {
      const bucket = match[1];
      const path = decodeURIComponent(match[2]);
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
      if (data?.signedUrl) return data.signedUrl;
      console.warn('Signed URL failed:', error?.message);
    }
  }
  // Fallback: try file_path directly with 'notes' bucket
  if (note.file_path) {
    const { data } = await supabase.storage.from('notes').createSignedUrl(note.file_path, 3600);
    if (data?.signedUrl) return data.signedUrl;
  }
  return null;
}

function ViewFileButton({ note }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setUrl(null);
    setError('');
    getFileUrl(note).then(u => {
      if (cancelled) return;
      if (u) setUrl(u);
      else setError('Dosyaya erişilemiyor.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [note.id]);

  if (loading) return <p style={{ fontSize: '0.78rem', color: '#7A8BAD' }}>URL hazırlanıyor...</p>;
  if (error) return <p style={{ fontSize: '0.78rem', color: '#D32F2F' }}>{error}</p>;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-admin btn-admin-outline"
      style={{ display: 'inline-block', textDecoration: 'none' }}
    >
      Dosyayı Görüntüle ↗
    </a>
  );
}

function aiStatusBadge(s) {
  const map = { approved: 'badge-green', pending: 'badge-orange', rejected: 'badge-red' };
  const labels = { approved: 'Uygun', pending: 'Beklemede', rejected: 'Reddedildi' };
  return <span className={`badge ${map[s] || 'badge-gray'}`}>{labels[s] || s || '-'}</span>;
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('tr-TR') : '-'; }

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterAI, setFilterAI] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('notes')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterDept) query = query.eq('department', filterDept);
      if (filterAI) query = query.eq('ai_status', filterAI);
      if (filterStatus === 'active') query = query.eq('is_deleted', false);
      if (filterStatus === 'deleted') query = query.eq('is_deleted', true);

      const { data, error } = await query;
      if (error) { console.error('Notes fetch error:', error); setLoading(false); return; }

      const userIds = [...new Set((data || []).map(n => n.user_id).filter(Boolean))];
      let ownerMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase.from('profiles').select('id, name').in('id', userIds);
        if (profiles) profiles.forEach(p => { ownerMap[p.id] = p.name; });
      }

      const enriched = (data || []).map(n => ({
        ...n,
        ownerName: ownerMap[n.user_id] || '-',
      }));

      const filtered = search
        ? enriched.filter(n => (n.title || '').toLowerCase().includes(search.toLowerCase()))
        : enriched;

      setNotes(filtered);
      const depts = [...new Set(enriched.map(n => n.department).filter(Boolean))];
      setDepartments(depts);
    } catch (e) {
      console.error('Notes error:', e);
    }
    setLoading(false);
  }, [search, filterDept, filterAI, filterStatus]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  async function handleToggleDelete(note, isDelete) {
    setActionLoading(true);
    const { error } = await supabase.from('notes').update({ is_deleted: isDelete }).eq('id', note.id);
    setActionLoading(false);
    if (!error) {
      setConfirmModal(null);
      fetchNotes();
      if (selectedNote?.id === note.id) setSelectedNote(prev => ({ ...prev, is_deleted: isDelete }));
    }
  }

  return (
    <div>
      <div className="admin-filter-bar">
        <input className="admin-search-input" placeholder="Başlık ile ara..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="admin-select" value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          <option value="">Tüm Bölümler</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="admin-select" value={filterAI} onChange={e => setFilterAI(e.target.value)}>
          <option value="">AI Durumu</option>
          <option value="approved">Uygun</option>
          <option value="pending">Beklemede</option>
          <option value="rejected">Reddedildi</option>
        </select>
        <select className="admin-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Durum</option>
          <option value="active">Aktif</option>
          <option value="deleted">Silinmiş</option>
        </select>
        <button className="btn-admin btn-admin-primary" onClick={fetchNotes}>Filtrele</button>
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Başlık</th>
                <th>Sahip</th>
                <th>Ders</th>
                <th>Bölüm</th>
                <th>Tip</th>
                <th>Sayfa</th>
                <th>AI Durumu</th>
                <th>Durum</th>
                <th>Tarih</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="admin-loading-row"><td colSpan={10}>Yükleniyor...</td></tr>
              ) : notes.length === 0 ? (
                <tr className="admin-loading-row"><td colSpan={10}>Not bulunamadı.</td></tr>
              ) : notes.map(n => (
                <tr key={n.id}>
                  <td>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5784E6', fontWeight: 600, padding: 0, fontSize: '0.875rem', fontFamily: 'inherit' }} onClick={() => setSelectedNote(n)}>
                      {n.title || '-'}
                    </button>
                  </td>
                  <td>{n.ownerName || '-'}</td>
                  <td>{n.course || '-'}</td>
                  <td>{n.department || '-'}</td>
                  <td><span className="badge badge-blue">{n.file_type || '-'}</span></td>
                  <td>{n.page_count || '-'}</td>
                  <td>{aiStatusBadge(n.ai_status)}</td>
                  <td>{n.is_deleted ? <span className="badge badge-red">Silinmiş</span> : <span className="badge badge-green">Aktif</span>}</td>
                  <td>{fmtDate(n.created_at)}</td>
                  <td>
                    {n.is_deleted ? (
                      <button className="btn-admin btn-admin-success" onClick={() => setConfirmModal({ note: n, action: 'restore' })}>Geri Yükle</button>
                    ) : (
                      <button className="btn-admin btn-admin-danger" onClick={() => setConfirmModal({ note: n, action: 'delete' })}>Sil</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-table-footer">Toplam {notes.length} not gösteriliyor</div>
      </div>

      {selectedNote && (
        <div className="admin-drawer-overlay" onClick={() => setSelectedNote(null)}>
          <div className="admin-drawer" onClick={e => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h3>Not Detayı</h3>
              <button className="admin-drawer-close" onClick={() => setSelectedNote(null)}>✕</button>
            </div>
            <div className="admin-drawer-body">
              <div className="detail-row"><label>Başlık</label><span>{selectedNote.title || '-'}</span></div>
              <div className="detail-row"><label>Açıklama</label><span>{selectedNote.description || '-'}</span></div>
              <div className="detail-row"><label>Sahip</label><span>{selectedNote.ownerName || '-'}</span></div>
              <div className="detail-row"><label>Ders</label><span>{selectedNote.course || '-'}</span></div>
              <div className="detail-row"><label>Bölüm</label><span>{selectedNote.department || '-'}</span></div>
              <div className="detail-row"><label>Dönem</label><span>{selectedNote.term || '-'}</span></div>
              <div className="detail-row"><label>Dosya Tipi</label><span>{selectedNote.file_type || '-'}</span></div>
              <div className="detail-row"><label>Sayfa Sayısı</label><span>{selectedNote.page_count || '-'}</span></div>
              <hr className="detail-divider" />
              <div className="detail-row"><label>AI Durumu</label><span>{aiStatusBadge(selectedNote.ai_status)}</span></div>
              <div className="detail-row"><label>AI Skoru</label><span>{selectedNote.ai_score ?? '-'}</span></div>
              {selectedNote.ai_reject_reason && <div className="detail-row"><label>Ret Nedeni</label><span>{selectedNote.ai_reject_reason}</span></div>}
              {selectedNote.ai_analyzed_at && <div className="detail-row"><label>AI Analiz Tarihi</label><span>{fmtDate(selectedNote.ai_analyzed_at)}</span></div>}
              <hr className="detail-divider" />
              <div className="detail-row">
                <label>Durum</label>
                <span>{selectedNote.is_deleted ? <span className="badge badge-red">Silinmiş</span> : <span className="badge badge-green">Aktif</span>}</span>
              </div>
              {(selectedNote.file_url || selectedNote.file_path) && (
                <ViewFileButton note={selectedNote} />
              )}
              <hr className="detail-divider" />
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedNote.is_deleted ? (
                  <button className="btn-admin btn-admin-success" onClick={() => setConfirmModal({ note: selectedNote, action: 'restore' })}>Geri Yükle</button>
                ) : (
                  <button className="btn-admin btn-admin-danger" onClick={() => setConfirmModal({ note: selectedNote, action: 'delete' })}>Notu Sil</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>{confirmModal.action === 'delete' ? 'Notu Sil' : 'Notu Geri Yükle'}</h3>
            <p>
              <strong>{confirmModal.note.title}</strong> notu{' '}
              {confirmModal.action === 'delete' ? 'silinecek' : 'geri yüklenecek'}. Devam etmek istiyor musunuz?
            </p>
            <div className="admin-modal-actions">
              <button className="btn-admin btn-admin-outline" onClick={() => setConfirmModal(null)}>İptal</button>
              <button
                className={`btn-admin ${confirmModal.action === 'delete' ? 'btn-admin-danger' : 'btn-admin-success'}`}
                onClick={() => handleToggleDelete(confirmModal.note, confirmModal.action === 'delete')}
                disabled={actionLoading}
              >
                {actionLoading ? 'İşleniyor...' : confirmModal.action === 'delete' ? 'Evet, Sil' : 'Evet, Geri Yükle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
